import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { AxiosError } from "axios";
import { FC, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { GetProgramFeeResponse } from "../../api/services/common/models";
import { useGetPageP1ContentMutation } from "../../api/services/pageContent/pageContentApi";
import {
  useGetCurrencies,
  useGetParticipantDetailsById,
  useGetProgramDetailById,
  useGetProgramFeeDetails,
  useGetProgramStatusListId,
} from "../../api/services/program/programApis";
import { MuiButtonTertiary } from "../../components";
import AccessDenied from "../../components/accessDenied/AccessDenied";
import { PAGE_IDS, PAGE_URLS } from "../../helpers/constants";
import {
  checkIsStatusCode403Forbidden,
  getFullName,
  getValueOrBlank,
  isNotEmpty,
} from "../../helpers/utils";
import { useSiteCore } from "../../hooks/useSiteCore";
import { useToast } from "../../hooks/useToast";
import {
  setCurrencyList,
  toggleLoader,
} from "../../redux/slices/appState/appStateSlice";
import {
  updateProgramCreatedDate,
  updateProgramStatus,
} from "../../redux/slices/program/programSlice";
import { CreateProgramGeneralDetail } from "../createProgram/components/createProgramGeneralDetail/CreateProgramGeneralDetail";
import { programSiteCoreKeys } from "../createProgram/siteCore/SiteCoreKeys";
import { InputTransferDetails } from "../programSearch/components/inputTransferDetails/InputTransferDetails";
import { ProgramAndParticipant } from "./components/programAndParticipant/ProgramAndParticipants";
import { ViewProgramFooter } from "./components/viewProgramFooter/ViewProgramFooter";
import { ViewProgramHeader } from "./components/viewProgramHeader/ViewProgramHeader";
import { ViewProgramWrapper } from "./ViewProgram.Style";

export const pageId = PAGE_IDS.VIEW_PROGRAM;

const ViewProgram: FC = () => {
  const [showTransferModal, setShowTransferModal] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { displayToast } = useToast();
  const { id } = useParams();
  const location = useLocation();
  const path = location?.pathname;
  const isFromCreateProjectChildPrograms =
    location?.state?.from === PAGE_URLS.CREATE_PROJECT;
  const isFromViewProjectChildPrograms =
    location?.state?.from === PAGE_URLS.VIEW_PROJECT;
  const isFromEditProjectChildPrograms =
    location?.state?.from === PAGE_URLS.EDIT_PROJECT;

  const [selectedTab, setSelectedTab] = useState(0);
  const [showAccessDenied, setShowAccessDenied] = useState(false);

  const {
    data: programDetail,
    isFetching: isProgramFetching,
    isError: isProgramError,
    error: programError,
    isSuccess: isProgramSuccess,
    refetchWithParams: refetchProgramDetail,
  } = useGetProgramDetailById();
  const {
    refetchWithParams: getParticipantDetailsByProgramId,
    data: participantDetail,
    isPending: isParticipantDetailFetching,
    isError: isParticipantDetailError,
    isSuccess: isParticipantSuccess,
  } = useGetParticipantDetailsById();
  const {
    data: programStatusList,
    isFetching: isProgramStatusListFetching,
    isError: isProgramStatusListError,
    refetchWithParams: refetchProgramStatusList,
  } = useGetProgramStatusListId();

  const {
    data: programFeeDetailsData,
    isFetching: isProgramFeeDetailsPending,
    isError: isProgramFeeDetailsError,
    refetchWithParams: getProgramFeeByProgramId,
  } = useGetProgramFeeDetails();

  const {
    mutate: getPageContent,
    data: pageContentData,
    isPending: isPageContentPending,
    isError,
  } = useGetPageP1ContentMutation();

  const { data: currenciesList, isSuccess: isCurrenciesListSuccess } =
    useGetCurrencies();

  useEffect(() => {
    if (id) {
      getProgramFeeByProgramId(id);
    }
  }, [id]);
  useEffect(() => {
    if (isCurrenciesListSuccess && isNotEmpty(currenciesList)) {
      dispatch(setCurrencyList(currenciesList));
    }
  }, [isCurrenciesListSuccess]);

  const pageContent = () => {
    getPageContent({
      PageName: "OnboardingPages",
      MarketName: getValueOrBlank(participantDetail?.marketName),
      language: participantDetail?.preferredLanguage,
      Persona: programDetail?.nextPersona ?? "",
    });
  };
  useEffect(() => {
    if (isProgramSuccess && programDetail) {
      setShowAccessDenied(false);
      dispatch(updateProgramCreatedDate(programDetail?.createdDate));
    }
  }, [isProgramSuccess, programDetail?.createdDate]);

  useEffect(() => {
    if (
      isParticipantSuccess &&
      isProgramSuccess &&
      participantDetail?.isRegisteredInNext
    ) {
      pageContent();
    }
  }, [
    isProgramSuccess,
    isParticipantSuccess,
    participantDetail?.isRegisteredInNext,
  ]);

  const { commonLabels, fetchLabel } = useSiteCore();

  useEffect(() => {
    if (isProgramError && programError) {
      // Handle different status codes
      if (checkIsStatusCode403Forbidden(programError as AxiosError)) {
        setShowAccessDenied(true);
      } else {
        displayToast(
          commonLabels.someThingWentWrongPleaseTryAgainLater,
          "error",
        );
      }

      dispatch(toggleLoader({ isVisible: false }));
    } else {
      setShowAccessDenied(false);
    }
  }, [isProgramError, programError]);

  useEffect(() => {
    if (
      isParticipantDetailError ||
      isProgramStatusListError ||
      isProgramFeeDetailsError ||
      isError
    ) {
      displayToast(commonLabels.someThingWentWrongPleaseTryAgainLater, "error");
      dispatch(toggleLoader({ isVisible: false }));
    }
  }, [
    isParticipantDetailError,
    isProgramStatusListError,
    isProgramFeeDetailsError,
    isError,
  ]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible:
          isProgramFetching ||
          isParticipantDetailFetching ||
          isProgramStatusListFetching ||
          isProgramFeeDetailsPending ||
          isPageContentPending,
        message: commonLabels.loadingPleaseWait,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [
    isProgramFetching,
    isParticipantDetailFetching,
    isProgramStatusListFetching,
    isProgramFeeDetailsPending,
    isPageContentPending,
  ]);

  const getProgramStatusListApi = () => {
    refetchProgramStatusList({ programId: id ?? "" });
  };

  useEffect(() => {
    if (id) {
      refetchProgramDetail(id);
      getParticipantDetailsByProgramId(id);
      getProgramStatusListApi();
    }
  }, [id]);

  useEffect(() => {
    const status = isNotEmpty(programDetail)
      ? getValueOrBlank(programDetail?.programStatus)
      : "";
    dispatch(updateProgramStatus(status));
  }, [programDetail]);

  const methods = useForm({
    mode: "all",
  });

  const handleCancel = () => {
    if (
      isFromCreateProjectChildPrograms ||
      isFromEditProjectChildPrograms ||
      isFromViewProjectChildPrograms
    ) {
      navigate(-1);
    } else {
      navigate(PAGE_URLS.SEARCH_PROGRAM);
    }
  };

  const handleEdit = () => {
    type StateType = { tabValue: number; from?: string };
    const state: StateType = { tabValue: selectedTab };
    if (isFromEditProjectChildPrograms || isFromViewProjectChildPrograms) {
      state.from = location?.state?.from;
    }

    navigate(`${PAGE_URLS.EDIT_PROGRAM}/${id}`, { state });
  };

  const navigateToProject = (projectId: string = "") => {
    navigate(`${PAGE_URLS.VIEW_PROJECT}/${projectId}`, {
      state: {
        projectId: projectId,
        programId: id,
        from: PAGE_URLS.VIEW_PROGRAM,
      },
    });
  };

  const onShowTransferHandler = () => {
    setShowTransferModal(true);
  };
  const participantName = getFullName(
    getValueOrBlank(participantDetail?.firstName),
    getValueOrBlank(participantDetail?.lastName),
  );

  if (showAccessDenied) {
    return <AccessDenied />;
  }

  return (
    <ViewProgramWrapper>
      {(isFromCreateProjectChildPrograms ||
        isFromEditProjectChildPrograms ||
        isFromViewProjectChildPrograms) && (
        <MuiButtonTertiary
          data-testid={`${pageId}-back`}
          onClick={handleCancel}
        >
          <ArrowBackIosIcon />
          {commonLabels.back}
        </MuiButtonTertiary>
      )}
      <ViewProgramHeader
        title={fetchLabel(
          programSiteCoreKeys.createProgramHeader,
          programSiteCoreKeys.viewProgramTitle,
        )}
        onEditHandler={handleEdit}
        status={programDetail?.programStatus}
        solutionOffering={programDetail?.solutionOffering}
        onShowTransferHandler={onShowTransferHandler}
        versionHistory={programDetail?.versions}
        isVersionLatest={
          !programDetail?.versions ||
          (programDetail?.versions &&
            path.includes(getValueOrBlank(programDetail?.versions[0].id)))
        }
        programId={id || ""}
        isFromCreateProjectChildPrograms={
          isFromCreateProjectChildPrograms || isFromViewProjectChildPrograms
        }
        isEditable={programDetail?.isEditable}
      />
      <FormProvider {...methods}>
        <CreateProgramGeneralDetail
          pageId={pageId}
          programDetail={programDetail}
          isViewProgram
          programId={id || ""}
          isFromEditProjectChildPrograms={
            isFromCreateProjectChildPrograms ||
            isFromViewProjectChildPrograms ||
            isFromEditProjectChildPrograms
          }
          onNavigateToProjectCallBack={navigateToProject}
        />
      </FormProvider>
      <FormProvider {...methods}>
        <ProgramAndParticipant
          pageId={pageId}
          participantDetail={participantDetail}
          programDetail={programDetail}
          programStatusList={programStatusList}
          refetchProgramStatusList={getProgramStatusListApi}
          selectedTab={selectedTab}
          setSelectedTab={setSelectedTab}
          programFeeDetailsData={
            programFeeDetailsData || ({} as GetProgramFeeResponse)
          }
          programId={id}
          data-testid={"transfer-participant-btn"}
          pageContentData={pageContentData}
        />
      </FormProvider>
      <ViewProgramFooter
        pageId={pageId}
        handleNavigateBack={handleCancel}
        handleEdit={handleEdit}
        isVersionLatest={
          !programDetail?.versions ||
          (programDetail?.versions &&
            path.includes(getValueOrBlank(programDetail?.versions[0].id)))
        }
        isEditable={programDetail?.isEditable}
      />
      {showTransferModal && (
        <InputTransferDetails
          pageId={pageId}
          data-testid={`${pageId}-inputTransferDetails`}
          open={showTransferModal}
          setShowInputTransferDetailsModal={setShowTransferModal}
          programId={id}
          participantName={participantName}
        />
      )}
    </ViewProgramWrapper>
  );
};

export default ViewProgram;
